import React, { useState, useEffect, useCallback } from 'react';
import { Building2, CreditCard, Wrench, Upload, Save, Image, Mail, MessageCircle, Send, Shield, MapPin, Clock, FileDown, ShieldCheck, ExternalLink, Copy } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';

const DAY_LABELS = {
  monday: 'Lundi', tuesday: 'Mardi', wednesday: 'Mercredi',
  thursday: 'Jeudi', friday: 'Vendredi', saturday: 'Samedi', sunday: 'Dimanche',
};

const AccessControlSection = () => {
  const [acSettings, setAcSettings] = useState(null);
  const [savingAc, setSavingAc] = useState(false);
  const [detectingGps, setDetectingGps] = useState(false);

  useEffect(() => {
    axios.get(`${API}/access-control/settings`).then(r => setAcSettings(r.data)).catch(() => {});
  }, []);

  const handleSaveAc = async () => {
    setSavingAc(true);
    try {
      const res = await axios.put(`${API}/access-control/settings`, acSettings);
      setAcSettings(res.data);
      toast.success('Controle d\'acces sauvegarde');
    } catch { toast.error('Erreur de sauvegarde'); }
    finally { setSavingAc(false); }
  };

  const detectLocation = () => {
    if (!navigator.geolocation) { toast.error('Geolocalisation non supportee'); return; }
    setDetectingGps(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setAcSettings(prev => ({
          ...prev,
          garage_location: {
            ...prev.garage_location,
            latitude: Math.round(pos.coords.latitude * 1000000) / 1000000,
            longitude: Math.round(pos.coords.longitude * 1000000) / 1000000,
          },
        }));
        toast.success('Position detectee');
        setDetectingGps(false);
      },
      () => { toast.error('Impossible de detecter la position'); setDetectingGps(false); },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const updateSchedule = (day, field, value) => {
    setAcSettings(prev => ({
      ...prev,
      schedule: { ...prev.schedule, [day]: { ...prev.schedule[day], [field]: value } },
    }));
  };

  if (!acSettings) return null;

  return (
    <Card className="p-5 border-l-4 border-l-red-500" data-testid="access-control-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
          <Shield className="w-4 h-4 text-red-500" /> Controle d'acces atelier
        </h3>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">{acSettings.enabled ? 'Actif' : 'Desactive'}</span>
            <Switch
              checked={acSettings.enabled}
              onCheckedChange={(v) => setAcSettings(p => ({ ...p, enabled: v }))}
              data-testid="ac-enabled-switch"
            />
          </div>
          <Button size="sm" onClick={handleSaveAc} disabled={savingAc} data-testid="save-ac-btn">
            <Save className="w-3 h-3 mr-1" /> {savingAc ? '...' : 'Sauvegarder'}
          </Button>
        </div>
      </div>

      <p className="text-xs text-slate-500 mb-4">
        Restreint l'acces a l'atelier par geolocalisation et horaires. Ne s'applique PAS aux administrateurs.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* GPS Location */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-red-500" /> Position du garage
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500">Latitude</label>
              <Input
                type="number" step="0.000001"
                value={acSettings.garage_location?.latitude ?? ''}
                onChange={e => setAcSettings(p => ({ ...p, garage_location: { ...p.garage_location, latitude: parseFloat(e.target.value) || null } }))}
                placeholder="46.992979"
                data-testid="ac-latitude"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500">Longitude</label>
              <Input
                type="number" step="0.000001"
                value={acSettings.garage_location?.longitude ?? ''}
                onChange={e => setAcSettings(p => ({ ...p, garage_location: { ...p.garage_location, longitude: parseFloat(e.target.value) || null } }))}
                placeholder="6.929174"
                data-testid="ac-longitude"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="text-xs text-slate-500">Rayon autorise (metres)</label>
              <Input
                type="number" min="10" max="1000"
                value={acSettings.garage_location?.radius_meters ?? 100}
                onChange={e => setAcSettings(p => ({ ...p, garage_location: { ...p.garage_location, radius_meters: parseInt(e.target.value) || 100 } }))}
                data-testid="ac-radius"
              />
            </div>
            <Button variant="outline" size="sm" onClick={detectLocation} disabled={detectingGps} className="mt-4" data-testid="detect-gps-btn">
              <MapPin className="w-3 h-3 mr-1" /> {detectingGps ? 'Detection...' : 'Ma position'}
            </Button>
          </div>
          {acSettings.garage_location?.latitude && acSettings.garage_location?.longitude && (
            <p className="text-[10px] text-green-600 font-medium">
              Position configuree : {acSettings.garage_location.latitude}, {acSettings.garage_location.longitude} (rayon {acSettings.garage_location.radius_meters}m)
            </p>
          )}
        </div>

        {/* Schedule */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-blue-500" /> Horaires d'acces
          </h4>
          <div className="space-y-2">
            {Object.entries(DAY_LABELS).map(([key, label]) => {
              const day = acSettings.schedule?.[key] || {};
              return (
                <div key={key} className="flex items-center gap-2" data-testid={`schedule-${key}`}>
                  <Switch
                    checked={day.enabled !== false}
                    onCheckedChange={(v) => updateSchedule(key, 'enabled', v)}
                    data-testid={`schedule-${key}-switch`}
                  />
                  <span className={`text-xs w-16 font-medium ${day.enabled !== false ? 'text-slate-700' : 'text-slate-400'}`}>{label}</span>
                  {day.enabled !== false ? (
                    <div className="flex items-center gap-1.5">
                      <Input
                        type="time" value={day.start || '07:00'}
                        onChange={e => updateSchedule(key, 'start', e.target.value)}
                        className="w-24 h-7 text-xs"
                        data-testid={`schedule-${key}-start`}
                      />
                      <span className="text-xs text-slate-400">-</span>
                      <Input
                        type="time" value={day.end || '19:30'}
                        onChange={e => updateSchedule(key, 'end', e.target.value)}
                        className="w-24 h-7 text-xs"
                        data-testid={`schedule-${key}-end`}
                      />
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400 italic">Ferme</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Card>
  );
};

const ServicePlusTariffsSection = () => {
  const [tariffs, setTariffs] = useState([]);
  const [savingSp, setSavingSp] = useState(false);
  const [newItem, setNewItem] = useState({ label: '', price: 0, description: '' });

  useEffect(() => {
    axios.get(`${API}/settings/service-plus-tariffs`)
      .then(r => setTariffs(r.data.tariffs || []))
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    setSavingSp(true);
    try {
      const res = await axios.put(`${API}/settings/service-plus-tariffs`, { tariffs });
      setTariffs(res.data.tariffs);
      toast.success('Tarifs Service Plus sauvegardes');
    } catch { toast.error('Erreur de sauvegarde'); }
    finally { setSavingSp(false); }
  };

  const updateTariff = (idx, field, value) => {
    setTariffs(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: field === 'price' ? (parseFloat(value) || 0) : value };
      return updated;
    });
  };

  const removeTariff = (idx) => {
    setTariffs(prev => prev.filter((_, i) => i !== idx));
  };

  const addTariff = () => {
    if (!newItem.label.trim()) { toast.error('Nom requis'); return; }
    const id = newItem.label.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_');
    setTariffs(prev => [...prev, { id, label: newItem.label, price: newItem.price, description: newItem.description }]);
    setNewItem({ label: '', price: 0, description: '' });
  };

  return (
    <Card className="p-5 border-l-4 border-l-emerald-500" data-testid="service-plus-tariffs-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
          <Shield className="w-4 h-4 text-emerald-500" /> Tarifs Service Plus
        </h3>
        <Button size="sm" onClick={handleSave} disabled={savingSp} data-testid="save-sp-tariffs-btn">
          <Save className="w-3 h-3 mr-1" /> {savingSp ? '...' : 'Sauvegarder'}
        </Button>
      </div>
      <p className="text-xs text-slate-500 mb-4">Forfaits HT modifiables. Les tarifs a 0.- ne seront pas affiches sur les fiches.</p>

      <div className="space-y-2">
        {tariffs.map((t, idx) => (
          <div key={t.id || idx} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2" data-testid={`sp-tariff-${t.id}`}>
            <Input
              value={t.label}
              onChange={e => updateTariff(idx, 'label', e.target.value)}
              className="flex-1 h-8 text-sm"
              data-testid={`sp-tariff-label-${idx}`}
            />
            <Input
              type="number" step="5" min="0"
              value={t.price}
              onChange={e => updateTariff(idx, 'price', e.target.value)}
              className="w-20 h-8 text-sm text-right font-bold"
              data-testid={`sp-tariff-price-${idx}`}
            />
            <span className="text-xs text-slate-500 shrink-0">CHF</span>
            <button onClick={() => removeTariff(idx)} className="text-red-400 hover:text-red-600 p-1" data-testid={`sp-tariff-remove-${idx}`}>
              <Wrench className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      {/* Add new */}
      <div className="mt-3 pt-3 border-t border-dashed border-slate-200">
        <p className="text-xs font-semibold text-slate-500 mb-2">Ajouter un forfait</p>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Nom du forfait"
            value={newItem.label}
            onChange={e => setNewItem(p => ({ ...p, label: e.target.value }))}
            className="flex-1 h-8 text-sm"
            data-testid="sp-new-label"
          />
          <Input
            type="number" step="5" min="0"
            placeholder="Prix"
            value={newItem.price || ''}
            onChange={e => setNewItem(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))}
            className="w-20 h-8 text-sm"
            data-testid="sp-new-price"
          />
          <Button size="sm" variant="outline" onClick={addTariff} data-testid="sp-add-btn">+</Button>
        </div>
      </div>
    </Card>
  );
};

const CompanySettingsPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [hasLogo, setHasLogo] = useState(false);
  const [logoKey, setLogoKey] = useState(0);
  const [form, setForm] = useState({
    company: { name: '', address: '', city: '', phone: '', fax: '', email: '', website: '' },
    labor_rates: [],
    bank_details: { holder: '', iban: '', bic: '', bank_name: '' },
    smtp: { server: '', port: 465, email: '', password: '', encryption: 'SSL/TLS', sender_name: '' },
    whatsapp_number: '',
  });

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/settings`);
      setForm({
        company: res.data.company || {},
        labor_rates: res.data.labor_rates || [],
        bank_details: res.data.bank_details || {},
        smtp: res.data.smtp || { server: '', port: 465, email: '', password: '', encryption: 'SSL/TLS', sender_name: '' },
        whatsapp_number: res.data.whatsapp_number || '',
      });
      setHasLogo(res.data.has_logo || false);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, form);
      toast.success('Parametres sauvegardes');
    } catch (err) { toast.error('Erreur de sauvegarde'); }
    finally { setSaving(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Le fichier doit etre une image'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/settings/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo mis a jour');
      setHasLogo(true);
      setLogoKey(k => k + 1);
    } catch (err) { toast.error('Erreur upload logo'); }
    finally { setUploading(false); }
  };

  const handleTestEmail = async () => {
    if (!form.smtp.email) { toast.error('Configurez d\'abord les parametres SMTP'); return; }
    setTesting(true);
    try {
      await axios.post(`${API}/notify/test-email`, { to_email: form.smtp.email });
      toast.success('Email de test envoyé !');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur envoi test'); }
    finally { setTesting(false); }
  };

  const updateCompany = (field, value) => setForm(f => ({ ...f, company: { ...f.company, [field]: value } }));
  const updateBank = (field, value) => setForm(f => ({ ...f, bank_details: { ...f.bank_details, [field]: value } }));
  const updateSmtp = (field, value) => setForm(f => ({ ...f, smtp: { ...f.smtp, [field]: value } }));
  const updateLaborRate = (idx, value) => {
    setForm(f => {
      const rates = [...f.labor_rates];
      rates[idx] = { ...rates[idx], hourly_rate: parseFloat(value) || 0 };
      return { ...f, labor_rates: rates };
    });
  };

  if (loading) return <AdminLayout auth={auth} onLogout={onLogout}><div className="p-6 text-center text-slate-500">{t('common.loading')}</div></AdminLayout>;

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-6 space-y-6" data-testid="company-settings-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="settings-title">{t('settings.title')}</h1>
            <p className="text-sm text-slate-500">Configuration garage, email, WhatsApp, banque, tarifs et securite</p>
          </div>
          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
          <Button variant="outline" onClick={() => window.open(`${API}/settings/export-pdf`, '_blank')} data-testid="export-pdf-btn">
            <FileDown className="w-4 h-4 mr-1" /> Exporter PDF
          </Button>
        </div>

        {/* Access Control Section - Full Width */}
        <AccessControlSection />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEFT COLUMN: Company Info + SMTP + WhatsApp */}
          <div className="space-y-6">
            {/* Company Info */}
            <Card className="p-5" data-testid="company-info-card">
              <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Informations de l'entreprise
              </h3>
              <div className="mb-4 pb-4 border-b">
                <label className="text-sm font-medium text-slate-700 mb-2 block">Logo</label>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden border">
                    {hasLogo ? (
                      <img key={logoKey} src={`${API}/settings/logo?t=${logoKey}`} alt="Logo" className="w-full h-full object-contain" />
                    ) : (
                      <Image className="w-8 h-8 text-slate-300" />
                    )}
                  </div>
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="logo-upload-input" />
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 rounded-md transition-colors">
                      <Upload className="w-3.5 h-3.5" /> {uploading ? 'Upload...' : 'Changer le logo'}
                    </span>
                  </label>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-slate-700">Nom de l'entreprise</label>
                  <Input value={form.company.name || ''} onChange={e => updateCompany('name', e.target.value)} data-testid="company-name" />
                </div>
                <div>
                  <label className="text-sm text-slate-700">Adresse</label>
                  <Input value={form.company.address || ''} onChange={e => updateCompany('address', e.target.value)} placeholder="Rue et numéro" data-testid="company-address" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-slate-700">NPA / Ville</label>
                    <Input value={form.company.city || ''} onChange={e => updateCompany('city', e.target.value)} data-testid="company-city" />
                  </div>
                  <div>
                    <label className="text-sm text-slate-700">Téléphone</label>
                    <Input value={form.company.phone || ''} onChange={e => updateCompany('phone', e.target.value)} data-testid="company-phone" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-slate-700">Email</label>
                    <Input value={form.company.email || ''} onChange={e => updateCompany('email', e.target.value)} data-testid="company-email" />
                  </div>
                  <div>
                    <label className="text-sm text-slate-700">Fax</label>
                    <Input value={form.company.fax || ''} onChange={e => updateCompany('fax', e.target.value)} data-testid="company-fax" />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-slate-700">Site web</label>
                  <Input value={form.company.website || ''} onChange={e => updateCompany('website', e.target.value)} data-testid="company-website" />
                </div>
              </div>
            </Card>

            {/* SMTP Config */}
            <Card className="p-5" data-testid="smtp-config-card">
              <h3 className="text-base font-semibold text-slate-900 mb-1 flex items-center gap-2">
                <Mail className="w-4 h-4" /> Configuration Email (SMTP)
              </h3>
              <p className="text-xs text-slate-500 mb-4">Pour l'envoi automatique d'emails (rappels, factures, devis)</p>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-slate-700">Serveur SMTP</label>
                    <Input value={form.smtp.server || ''} onChange={e => updateSmtp('server', e.target.value)}
                      placeholder="mail.garage-des-vignes.ch" data-testid="smtp-server" />
                  </div>
                  <div>
                    <label className="text-sm text-slate-700">Port</label>
                    <Input type="number" value={form.smtp.port || 465} onChange={e => updateSmtp('port', parseInt(e.target.value) || 465)}
                      data-testid="smtp-port" />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-slate-700">Email d'envoi</label>
                  <Input value={form.smtp.email || ''} onChange={e => updateSmtp('email', e.target.value)}
                    placeholder="info@garage-des-vignes.ch" data-testid="smtp-email" />
                </div>
                <div>
                  <label className="text-sm text-slate-700">Mot de passe</label>
                  <Input type="password" value={form.smtp.password || ''} onChange={e => updateSmtp('password', e.target.value)}
                    placeholder="Mot de passe email" data-testid="smtp-password" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-slate-700">Chiffrement</label>
                    <Select value={form.smtp.encryption || 'SSL/TLS'} onValueChange={v => updateSmtp('encryption', v)}>
                      <SelectTrigger data-testid="smtp-encryption"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SSL/TLS">SSL/TLS (port 465)</SelectItem>
                        <SelectItem value="STARTTLS">STARTTLS (port 587)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-slate-700">Nom expediteur</label>
                    <Input value={form.smtp.sender_name || ''} onChange={e => updateSmtp('sender_name', e.target.value)}
                      placeholder="Nom de votre entreprise" data-testid="smtp-sender-name" />
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={handleTestEmail} disabled={testing} data-testid="test-email-btn">
                  <Send className="w-3.5 h-3.5 mr-1" /> {testing ? 'Envoi...' : 'Envoyer un email de test'}
                </Button>
              </div>
            </Card>

            {/* SendGrid Domain Authentication Guide */}
            <Card className="p-5 border-amber-200 bg-amber-50/50" data-testid="sendgrid-domain-guide">
              <h3 className="text-base font-semibold text-slate-900 mb-1 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-amber-600" /> Authentification domaine email
              </h3>
              <p className="text-xs text-slate-500 mb-4">
                Pour eviter que vos emails soient filtres comme spam, ajoutez ces enregistrements DNS chez votre hebergeur de domaine.
              </p>
              <div className="space-y-3">
                <div className="text-xs space-y-2">
                  <p className="font-semibold text-slate-700">Etapes a suivre :</p>
                  <ol className="list-decimal list-inside space-y-2 text-slate-600">
                    <li>Connectez-vous a <a href="https://app.sendgrid.com/settings/sender_auth/domain/new" target="_blank" rel="noreferrer" className="text-blue-600 underline hover:text-blue-800">SendGrid &gt; Authentification de domaine <ExternalLink className="w-3 h-3 inline ml-0.5" /></a></li>
                    <li>Entrez votre domaine (ex: <span className="font-mono bg-white px-1.5 py-0.5 rounded border">garage-des-vignes.ch</span>)</li>
                    <li>SendGrid vous donnera <strong>3 enregistrements CNAME</strong> a ajouter</li>
                    <li>Connectez-vous a votre hebergeur DNS (Infomaniak, Hostpoint, etc.)</li>
                    <li>Ajoutez les 3 CNAME dans votre zone DNS</li>
                    <li>Revenez sur SendGrid et cliquez <strong>"Verifier"</strong></li>
                  </ol>
                </div>
                <div className="bg-white rounded-lg border p-3 mt-3">
                  <p className="text-xs font-semibold text-slate-700 mb-2">Exemple d'enregistrements CNAME :</p>
                  <div className="space-y-1.5 font-mono text-xs">
                    <div className="flex items-center gap-2 bg-slate-50 p-2 rounded">
                      <span className="text-blue-600 font-semibold">CNAME</span>
                      <span className="text-slate-500">em1234.votre-domaine.ch</span>
                      <span className="text-slate-400">&rarr;</span>
                      <span className="text-emerald-600">u1234.wl.sendgrid.net</span>
                    </div>
                    <div className="flex items-center gap-2 bg-slate-50 p-2 rounded">
                      <span className="text-blue-600 font-semibold">CNAME</span>
                      <span className="text-slate-500">s1._domainkey.votre-domaine.ch</span>
                      <span className="text-slate-400">&rarr;</span>
                      <span className="text-emerald-600">s1.domainkey.u1234.wl.sendgrid.net</span>
                    </div>
                    <div className="flex items-center gap-2 bg-slate-50 p-2 rounded">
                      <span className="text-blue-600 font-semibold">CNAME</span>
                      <span className="text-slate-500">s2._domainkey.votre-domaine.ch</span>
                      <span className="text-slate-400">&rarr;</span>
                      <span className="text-emerald-600">s2.domainkey.u1234.wl.sendgrid.net</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mt-2 italic">Les valeurs exactes seront fournies par SendGrid lors de la configuration.</p>
                </div>
                <div className="flex gap-2 mt-2">
                  <a href="https://app.sendgrid.com/settings/sender_auth" target="_blank" rel="noreferrer">
                    <Button variant="outline" size="sm" data-testid="sendgrid-auth-link">
                      <ExternalLink className="w-3.5 h-3.5 mr-1" /> Ouvrir SendGrid
                    </Button>
                  </a>
                </div>
              </div>
            </Card>

            {/* WhatsApp Number */}
            <Card className="p-5" data-testid="whatsapp-config-card">
              <h3 className="text-base font-semibold text-slate-900 mb-1 flex items-center gap-2">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </h3>
              <p className="text-xs text-slate-500 mb-4">Numéro utilisé pour les envois WhatsApp</p>
              <div>
                <label className="text-sm text-slate-700">Numéro WhatsApp</label>
                <Input value={form.whatsapp_number || ''} onChange={e => setForm(f => ({ ...f, whatsapp_number: e.target.value }))}
                  placeholder="+41 32 751 22 04" data-testid="whatsapp-number" />
              </div>
            </Card>
          </div>

          {/* RIGHT COLUMN: Bank + Labor Rates */}
          <div className="space-y-6">
            <Card className="p-5" data-testid="bank-details-card">
              <h3 className="text-base font-semibold text-slate-900 mb-1 flex items-center gap-2">
                <CreditCard className="w-4 h-4" /> Coordonnées bancaires
              </h3>
              <p className="text-xs text-slate-500 mb-4">Affichees en bas des factures PDF</p>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-slate-700">Titulaire du compte</label>
                  <Input value={form.bank_details?.holder || ''} onChange={e => updateBank('holder', e.target.value)}
                    placeholder="Nom du titulaire" data-testid="bank-holder" />
                </div>
                <div>
                  <label className="text-sm text-slate-700">IBAN</label>
                  <Input value={form.bank_details?.iban || ''} onChange={e => updateBank('iban', e.target.value)}
                    placeholder="CH93 0076 2011 6238 5295 7" data-testid="bank-iban" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-slate-700">BIC / SWIFT</label>
                    <Input value={form.bank_details?.bic || ''} onChange={e => updateBank('bic', e.target.value)}
                      placeholder="BCVLCH2L" data-testid="bank-bic" />
                  </div>
                  <div>
                    <label className="text-sm text-slate-700">Nom de la banque</label>
                    <Input value={form.bank_details?.bank_name || ''} onChange={e => updateBank('bank_name', e.target.value)}
                      placeholder="Banque Cantonale Vaudoise" data-testid="bank-name" />
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-5" data-testid="labor-rates-card">
              <h3 className="text-base font-semibold text-slate-900 mb-1 flex items-center gap-2">
                <Wrench className="w-4 h-4" /> Tarifs main d'oeuvre
              </h3>
              <p className="text-xs text-slate-500 mb-4">CHF/h. Les tarifs a 0 ne sont pas proposes dans les devis.</p>
              <div className="space-y-3">
                {(form.labor_rates || []).map((rate, idx) => (
                  <div key={rate.type} className="flex items-center gap-3" data-testid={`labor-rate-${rate.type}`}>
                    <label className="text-sm text-slate-700 w-48 shrink-0">{rate.label}</label>
                    <Input type="number" step="0.50" min="0" value={rate.hourly_rate}
                      onChange={e => updateLaborRate(idx, e.target.value)} className="w-28"
                      data-testid={`labor-rate-input-${rate.type}`} />
                    <span className="text-xs text-slate-500 shrink-0">CHF/h</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Service Plus Tariffs */}
            <ServicePlusTariffsSection />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CompanySettingsPage;
